import { forwardRef, useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthUser } from "../contexts/AuthUserContext";
import { useLocalization } from "../contexts/LocalizationContext";
import { useShop } from "../contexts/ShopContext";
import { CHAT_CONTEXT_PICK_MODE_EVENT } from "../utils/chatContextDrag";
import SelectedActionModal from "./home/SelectedActionModal";
import AppRightRailAssessment from "./AppRightRailAssessment";
import AppRightRailStrategy from "./AppRightRailStrategy";
import AppRightRailBrandKnowledge from "./AppRightRailBrandKnowledge";
import AppRightRailReportSection from "./AppRightRailReportSection";
import AppRightRailTaskAgentSection from "./AppRightRailTaskAgentSection";
import {
  GET_ACTIONS_GROUPED_BY_FOCUS_AREA,
} from "../graphql/queries";
import { collectionByKey } from "../pages/ReportsPage/reportViewUtils";
import {
  ASSESSMENT_SUMMARY_ITEMS,
  formatAssessmentDateLabel,
} from "../utils/assessmentSummaryConfig";
import { getLatestReportByCollection } from "../utils/api/reportApi";
import styles from "./AppShell.module.css";

const buildReportOpenTraceId = (key) =>
  `report-open:${key}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
const reportOpenNow = () => Date.now();
const buildAssessmentHeaderSeed = (item) => ({
  summaryId: item.id,
  title: item.label,
  latestUpdatedAt: item.latestUpdatedAt || null,
  source: "right-rail",
  clickedAt: Date.now(),
});

const REPORTS = [
  // { key: "benchmark", label: "Store summary" },
  { key: "meta", label: "Meta ads", refreshKey: "shop_meta" },
  // { key: "tiktok_ads", label: "TikTok ads report", comingSoon: true },
  { key: "shop_seo_reports", label: "SEO / AEO Audit", hasDropdown: true },
  { key: "google_search_console", label: "Google search console", refreshKey: "google_search_console" },
  { key: "google_merchant_center", label: "Google merchant center", refreshKey: "google_merchant_center" },
  { key: "revenue_reality", label: "Revenue reality", refreshKey: "revenue_reality_full_report" },
  { key: "socialMedia", label: "Social media" },
  { key: "market", label: "Market analysis", refreshKey: "shop_market_analysis" },
  { key: "target_audience", label: "Target audience", refreshKey: "shop_target_audience" },
  { key: "competitor", label: "Competitor analysis" },
  // { key: "yearlyContent", label: "Monthly events calendar" },
];


const REPORT_COLLECTION_CANDIDATES = {
  benchmark: ["shop_benchmark_report"],
  meta: ["shop_meta"],
  shop_seo_reports: ["shop_seo_reports"],
  google_search_console: [ "google_search_console"],
  google_merchant_center: ["google_merchant_center"],
  revenue_reality: ["revenue_reality_full_report"],
  socialMedia: ["social_media_report"],
  market: ["shop_market_analysis"],
  target_audience: ["shop_target_audience"],
  competitor: ["shop_competitor_analysis"],
  yearlyContent: [ "yearly_content_calendar"],
  dailyOverview: ["shop_daily_overview"],
};

const PRIMARY_COLLECTIONS = new Set([
  // "yearly_content_calendar",
  "shop_competitor_analysis",
  "shop_target_audience",
  "shop_market_analysis",
  "revenue_reality_full_report",
  "google_merchant_center",
  "google_search_console",
  "shop_meta",
  "shop_benchmark_report",
  "shop_seo_reports",
  "foundational_business_reality_summary",
  "shop_daily_overview",
  "social_media_report",
  "conversion_and_pdp_report",
]);

// All unique collections needed for freshness dates — restricted to primary collections only
const RIGHT_RAIL_COLLECTIONS = Array.from(new Set([
  ...Object.values(REPORT_COLLECTION_CANDIDATES).flat(),
  ...ASSESSMENT_SUMMARY_ITEMS.flatMap((item) => item.sourceCollections || []),
])).filter((col) => PRIMARY_COLLECTIONS.has(col));

const CHANNEL_HIGHLIGHTS = {
  main: {
    assessments: ["performance", "revenue", "market"],
    reports: ["benchmark", "meta", "revenue_reality"],
  },
  // email: {
  //   assessments: ["retention", "content"],
  //   reports: ["benchmark"],
  // },
  "paid-ads": {
    assessments: ["performance", "revenue", "conversion"],
    reports: ["meta", "revenue_reality", "target_audience"],
  },
  "meta-ads": {
    assessments: ["performance", "revenue", "conversion"],
    reports: ["meta"],
  },
  // "google-ads": {
  //   assessments: ["performance", "conversion"],
  //   reports: ["target_audience"],
  // },
  // "tiktok-ads": {
  //   assessments: ["performance", "conversion"],
  //   reports: ["target_audience"],
  // },
  social: {
    assessments: ["social"],
    reports: ["socialMedia", "target_audience"],
  },
  // "content-cal": {
  //   assessments: ["content"],
  //   reports: ["yearlyContent"],
  // },
  seo: {
    assessments: ["visibility"],
    reports: ["google_search_console", "market"],
  },
  // google: {
  //   assessments: ["conversion", "visibility"],
  //   reports: ["google_search_console", "google_merchant_center"],
  // },
  // "google-search-console": {
  //   assessments: ["visibility"],
  //   reports: ["google_search_console"],
  // },
  // "google-merchant-center": {
  //   assessments: ["conversion", "visibility"],
  //   reports: ["google_merchant_center"],
  // },
};

const normStatus = (value) => String(value || "").trim().toLowerCase();

const isCompletedTask = (taskNode) => taskNode?.isCompleted === true;
const isIgnoredTask = (taskNode) => normStatus(taskNode?.status) === "ignored";
const isRunningTask = (taskNode) => normStatus(taskNode?.status) === "running";
const isDoneTask = (taskNode) => isCompletedTask(taskNode) || isIgnoredTask(taskNode);
const isPendingTask = (taskNode) => !isDoneTask(taskNode) && !isRunningTask(taskNode);
const normalizeRailText = (value) => String(value || "").trim();
const normalizeTaskId = (taskNode, fallback = "") =>
  String(taskNode?.id || taskNode?._id || fallback || "").trim();

const isProductRailAction = (action = {}) => {
  const focusArea = normalizeRailText(action?.focusArea).toLowerCase();
  const department = normalizeRailText(action?.department).toLowerCase();
  return (
    Boolean(action?.product_id) &&
    (department === "product_intel" ||
      focusArea.includes("pdp") ||
      focusArea.includes("product"))
  );
};

const extractRailProductTitle = (action = {}) => {
  const direct =
    action?.product_name ||
    action?.productTitle ||
    action?.product_title ||
    action?.product?.title;
  if (direct) return normalizeRailText(direct);

  const descriptionMatch = String(action?.description || "").match(/^Product:\s*(.+)$/im);
  if (descriptionMatch?.[1]) return descriptionMatch[1].trim();

  const title = normalizeRailText(action?.title).replace(/^\s*\[Product\]\s*/i, "");
  const colonIndex = title.indexOf(":");
  if (colonIndex !== -1 && colonIndex < title.length - 1) {
    return title.substring(colonIndex + 1).trim();
  }
  return title || "Product";
};

const isBlogRailAction = (action = {}) =>
  Boolean(action?.blog_id || action?.article_id) ||
  /^\s*\[Blog\]/i.test(String(action?.title || ""));

const extractRailBlogTitle = (action = {}) => {
  const direct =
    action?.article_title ||
    action?.articleTitle ||
    action?.blog_title ||
    action?.blogTitle ||
    action?.article?.title ||
    action?.blog?.title;
  if (direct) return normalizeRailText(direct);

  const title = normalizeRailText(action?.title).replace(/^\s*\[Blog\]\s*/i, "");
  const lastColonIndex = title.lastIndexOf(":");
  if (lastColonIndex !== -1) {
    const possibleTaskName = title.slice(lastColonIndex + 1).trim();
    const looksLikeTaskName =
      possibleTaskName.length <= 90 &&
      /^(add|create|improve|test|request|update|fix|generate|write|optimi[sz]e|publish|refresh)\b/i.test(
        possibleTaskName,
      );
    if (looksLikeTaskName) return title.slice(0, lastColonIndex).trim() || "Blog";
  }

  return title || "Blog";
};

const cleanRailTaskTitle = (value) => {
  const title = normalizeRailText(value)
    .replace(/^\s*\[Blog\]\s*/i, "")
    .replace(/^\s*\[Product\]\s*/i, "");
  return title || "Task";
};

const buildRailSearchText = ({ focusArea, actions = [], title = "", extra = [] }) =>
  [
    focusArea,
    title,
    ...extra,
    ...actions.flatMap((action) => [
      action?.title,
      action?.description,
      action?.focusArea,
      action?.department,
      action?.product_name,
      action?.productTitle,
      action?.product_title,
      action?.article_title,
      action?.articleTitle,
      action?.blog_title,
      action?.blogTitle,
      ...(Array.isArray(action?.tasks)
        ? action.tasks.flatMap((taskNode) => [
            taskNode?.task_title,
            taskNode?.title,
            taskNode?.short_description,
            taskNode?.description,
            taskNode?.status,
          ])
        : []),
    ]),
  ]
    .filter(Boolean)
    .join(" ");

const getLatestIso = (values = []) =>
  values
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b - a)[0]
    ?.toISOString() || "";

const formatShortDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const formatLongDate = (value, fallback = "Earlier") => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const AppRightRail = forwardRef(function AppRightRail(
  {
    activeChannelId,
    activeSubchannelId,
    rightRailNavItems,
    handleRightRailNav,
  },
  ref,
) {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthUser();
  const { shopData } = useShop();
  const [openSections, setOpenSections] = useState({
    assessments: true,
    strategy: false,
    brand: false,
    reports: true,
  });
  const [openReportDropdown, setOpenReportDropdown] = useState(null);
  const [taskAgentTab, setTaskAgentTab] = useState("active");
  const [chatContextPickMode, setChatContextPickMode] = useState(false);
  const [selectedTaskRow, setSelectedTaskRow] = useState(null);
  const [selectedTaskActionId, setSelectedTaskActionId] = useState(null);
  const [selectedTaskModalTab, setSelectedTaskModalTab] = useState("");
  const userId = user?._id || user?.id || user?.userId || "";
  const authToken = user?.token;
  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shopData?._id)
    ? shopData._id
    : isMongoId(shopData?.id)
      ? shopData.id
      : null;
  const hasCommerceConnection = Boolean(
    user?.shopifyToken ||
      shopData?._id ||
      shopData?.id ||
      shopData?.myshopifyDomain ||
      (Array.isArray(user?.authProviders) &&
        user.authProviders.some((item) => item?.provider === "shopify")),
  );
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  // Track which assessment row matches the current page so we can show it as selected
  const activeSummaryId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("summary") || null;
  }, [location.search]);

  const assessmentNavItem = useMemo(
    () => rightRailNavItems.find((item) => item.iconType === "assessment"),
    [rightRailNavItems],
  );
  const strategyNavItem = useMemo(
    () => rightRailNavItems.find((item) => item.iconType === "strategy"),
    [rightRailNavItems],
  );
  const { data: actionsData, refetch: refetchActions } = useQuery(GET_ACTIONS_GROUPED_BY_FOCUS_AREA, {
    skip: !userId || !authToken,
    variables: { userId },
    context: { headers },
    fetchPolicy: "cache-and-network",
  });
  const [reports, setReports] = useState([]);
  useEffect(() => {
    if (!userId || !authToken || !hasCommerceConnection) {
      setReports([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      RIGHT_RAIL_COLLECTIONS.map((collection) =>
        getLatestReportByCollection({ collection, userId, shopId: shopId || undefined, authToken })
          .then((res) => res?.report || null)
          .catch(() => null)
      )
    ).then((results) => {
      if (!cancelled) setReports(results.filter(Boolean));
    });
    return () => { cancelled = true; };
  }, [userId, shopId, authToken, hasCommerceConnection]);

  useEffect(() => {
    const handleReportRefreshed = (event) => {
      const report = event?.detail?.report;
      const collectionName =
        event?.detail?.collectionName || report?.data?.collection_name || "";
      if (!collectionName) return;

      if (report?.data?.collection_name === collectionName) {
        setReports((current) => {
          const next = current.filter(
            (item) => item?.data?.collection_name !== collectionName,
          );
          return [...next, report];
        });
        return;
      }

      if (!userId || !authToken || !hasCommerceConnection) return;
      getLatestReportByCollection({
        collection: collectionName,
        userId,
        shopId: shopId || undefined,
        authToken,
      })
        .then((res) => res?.report || null)
        .then((latestReport) => {
          if (!latestReport) return;
          setReports((current) => {
            const next = current.filter(
              (item) => item?.data?.collection_name !== collectionName,
            );
            return [...next, latestReport];
          });
        })
        .catch(() => {});
    };

    window.addEventListener("zeqi:report-refreshed", handleReportRefreshed);
    return () =>
      window.removeEventListener("zeqi:report-refreshed", handleReportRefreshed);
  }, [userId, shopId, authToken, hasCommerceConnection]);

  useEffect(() => {
    const handlePickMode = (event) => {
      setChatContextPickMode(Boolean(event?.detail?.enabled));
    };
    window.addEventListener(CHAT_CONTEXT_PICK_MODE_EVENT, handlePickMode);
    return () =>
      window.removeEventListener(CHAT_CONTEXT_PICK_MODE_EVENT, handlePickMode);
  }, []);
  const effectiveContextId = activeSubchannelId || activeChannelId;
  const focusAreas = useMemo(
    () => actionsData?.getActionsGroupedByFocusArea?.focusAreas ?? [],
    [actionsData],
  );
  const reportByCollection = useMemo(() => {
    const idx = {};
    for (const report of reports) {
      const collection = report?.data?.collection_name;
      if (!collection) continue;
      const current = idx[collection];
      if (
        !current ||
        new Date(report.updatedAt || report.createdAt || 0) > new Date(current.updatedAt || current.createdAt || 0)
      ) {
        idx[collection] = report;
      }
    }
    return idx;
  }, [reports]);
  const reportsWithMeta = useMemo(
    () =>
      REPORTS.map((report) => {
        const primaryCollection = collectionByKey[report.key];
        const candidateCollections = [
          ...(REPORT_COLLECTION_CANDIDATES[report.key] || []),
          ...(primaryCollection ? [primaryCollection] : []),
        ].filter(Boolean);
        const latestUpdatedAt = candidateCollections
          .map((collection) => reportByCollection[collection])
          .filter(Boolean)
          .map((item) => item?.updatedAt || item?.createdAt || item?.createdDate || null)
          .filter(Boolean)
          .sort((a, b) => new Date(b) - new Date(a))[0];

        return {
          ...report,
          meta: formatAssessmentDateLabel(latestUpdatedAt),
          latestUpdatedAt: latestUpdatedAt || null,
          latestReportId: candidateCollections
            .map((collection) => reportByCollection[collection])
            .filter(Boolean)
            .sort(
              (left, right) =>
                new Date(right?.updatedAt || right?.createdAt || 0) -
                new Date(left?.updatedAt || left?.createdAt || 0)
            )[0]?._id ||
            candidateCollections
              .map((collection) => reportByCollection[collection])
              .filter(Boolean)
              .sort(
                (left, right) =>
                  new Date(right?.updatedAt || right?.createdAt || 0) -
                  new Date(left?.updatedAt || left?.createdAt || 0)
              )[0]?.id ||
            null,
          collectionName: candidateCollections.find(
            (collection) => reportByCollection[collection]
          ) || primaryCollection || null,
        };
      }),
    [reportByCollection],
  );
  const assessments = useMemo(
    () =>
      ASSESSMENT_SUMMARY_ITEMS.map((item) => {
        const latestUpdatedAt = item.sourceCollections
          .map((collection) => reportByCollection[collection])
          .filter(Boolean)
          .map((report) => report?.updatedAt || report?.createdAt || report?.createdDate || null)
          .filter(Boolean)
          .sort((a, b) => new Date(b) - new Date(a))[0];
        return {
          ...item,
          meta: formatAssessmentDateLabel(latestUpdatedAt),
          latestUpdatedAt: latestUpdatedAt || null,
        };
      }),
    [reportByCollection],
  );

  const highlightedAssessmentIds = useMemo(
    () =>
      effectiveContextId === "main"
        ? new Set()
        : new Set(CHANNEL_HIGHLIGHTS[effectiveContextId]?.assessments || []),
    [effectiveContextId],
  );
  const highlightedReportKeys = useMemo(
    () =>
      effectiveContextId === "main"
        ? new Set()
        : new Set(CHANNEL_HIGHLIGHTS[effectiveContextId]?.reports || []),
    [effectiveContextId],
  );
  const activeTaskGroups = useMemo(() => {
    const groups = new Map();
    focusAreas.forEach((group) => {
      const focusArea =
        String(group?.focusArea || "").trim() || "General";
      const productBuckets = new Map();
      const blogBuckets = new Map();
      const directItems = [];

      (group?.actions || []).forEach((action) => {
        const tasks = Array.isArray(action?.tasks) ? action.tasks : [];
        const pendingTasks = tasks.filter((taskNode) => isPendingTask(taskNode));
        const runningTasks = tasks.filter((taskNode) => isRunningTask(taskNode));
        if (!pendingTasks.length && !runningTasks.length) return;

        const actionId = action?.id || action?._id || `${focusArea}-action`;
        const activeTasks = [...runningTasks, ...pendingTasks];
        const latestStamp = getLatestIso([
          ...runningTasks.map((taskNode) => taskNode?.updatedAt || taskNode?.createdAt),
          ...pendingTasks.map((taskNode) => taskNode?.updatedAt || taskNode?.createdAt),
          action?.updatedAt,
          action?.createdAt,
        ]);

        if (isProductRailAction(action)) {
          const productId = String(action?.product_id || "").trim();
          const productKey = `product-${productId || extractRailProductTitle(action).toLowerCase()}`;
          if (!productBuckets.has(productKey)) {
            productBuckets.set(productKey, {
              id: productKey,
              actionIds: [],
              actions: [],
              title: extractRailProductTitle(action),
              runningCount: 0,
              pendingCount: 0,
              latestStamp: "",
            });
          }
          const bucket = productBuckets.get(productKey);
          bucket.actionIds.push(actionId);
          bucket.actions.push(action);
          bucket.runningCount += runningTasks.length;
          bucket.pendingCount += pendingTasks.length;
          bucket.latestStamp = getLatestIso([bucket.latestStamp, latestStamp]);
          return;
        }

        if (isBlogRailAction(action)) {
          const articleId = String(action?.article_id || "").trim();
          const shopifyArticleId = String(action?.shopifyArticleId || "").trim();
          const blogTitle = extractRailBlogTitle(action);
          const blogKey = `blog-article-${articleId || shopifyArticleId || blogTitle.toLowerCase()}`;
          if (!blogBuckets.has(blogKey)) {
            blogBuckets.set(blogKey, {
              id: blogKey,
              actionIds: [],
              actions: [],
              title: blogTitle,
              runningCount: 0,
              pendingCount: 0,
              latestStamp: "",
            });
          }
          const bucket = blogBuckets.get(blogKey);
          bucket.actionIds.push(actionId);
          bucket.actions.push(action);
          bucket.runningCount += runningTasks.length;
          bucket.pendingCount += pendingTasks.length;
          bucket.latestStamp = getLatestIso([bucket.latestStamp, latestStamp]);
          return;
        }

        directItems.push({
          id: actionId,
          actionId,
          title: action?.title || focusArea,
          statusLabel: runningTasks.length
            ? "In progress"
            : `${activeTasks.length} task${activeTasks.length === 1 ? "" : "s"}`,
          isRunning: runningTasks.length > 0,
          dateLabel: formatShortDate(latestStamp),
          row: {
            focusArea,
            actions: [action],
          },
          searchText: buildRailSearchText({
            focusArea,
            actions: [action],
            title: action?.title || focusArea,
            extra: [runningTasks.length ? "in progress" : "pending"],
          }),
        });
      });

      const productItems = Array.from(productBuckets.values()).map((bucket) => {
        const totalTasks = bucket.runningCount + bucket.pendingCount;
        return {
          id: bucket.id,
          actionId: bucket.actionIds[0] || null,
          title: bucket.title,
          statusLabel: bucket.runningCount
            ? `${bucket.runningCount} in progress · ${totalTasks} total`
            : `${totalTasks} task${totalTasks === 1 ? "" : "s"}`,
          isRunning: bucket.runningCount > 0,
          dateLabel: formatShortDate(bucket.latestStamp),
          row: {
            focusArea,
            actions: bucket.actions,
          },
          searchText: buildRailSearchText({
            focusArea,
            actions: bucket.actions,
            title: bucket.title,
            extra: ["product", `${totalTasks} tasks`],
          }),
        };
      });

      const blogItems = Array.from(blogBuckets.values()).map((bucket) => {
        const totalTasks = bucket.runningCount + bucket.pendingCount;
        return {
          id: bucket.id,
          actionId: bucket.actionIds[0] || null,
          title: bucket.title,
          statusLabel: bucket.runningCount
            ? `${bucket.runningCount} in progress · ${totalTasks} total`
            : `${totalTasks} task${totalTasks === 1 ? "" : "s"}`,
          isRunning: bucket.runningCount > 0,
          dateLabel: formatShortDate(bucket.latestStamp),
          row: {
            focusArea,
            actions: bucket.actions,
          },
          searchText: buildRailSearchText({
            focusArea,
            actions: bucket.actions,
            title: bucket.title,
            extra: ["blog", "article", `${totalTasks} tasks`],
          }),
        };
      });

      const items = [...productItems, ...blogItems, ...directItems];
      if (items.length) {
        if (!groups.has(focusArea)) groups.set(focusArea, []);
        groups.get(focusArea).push(...items);
      }
    });

    return Array.from(groups.entries())
      .map(([focusArea, tasks]) => ({
        focusArea,
        tasks: tasks.sort(
          (a, b) =>
            Number(b.isRunning) - Number(a.isRunning) ||
            String(a.title || "").localeCompare(String(b.title || "")),
        ),
      }))
      .filter((group) => group.tasks.length > 0);
  }, [focusAreas]);
  const taskLogGroups = useMemo(() => {
    const byDate = new Map();
    focusAreas.forEach((group) => {
      const focusArea =
        String(group?.focusArea || "").trim() || "General";
      (group?.actions || []).forEach((action) => {
        const tasks = Array.isArray(action?.tasks) ? action.tasks : [];
        if (!tasks.length) return;

        const actionId = action?.id || action?._id || `${focusArea}-action`;
        const doneTasks = tasks.filter((taskNode) => isDoneTask(taskNode));
        if (!doneTasks.length) return;

        doneTasks.forEach((taskNode, index) => {
          const taskId = normalizeTaskId(taskNode, `${actionId}-done-${index}`);
          const stampCandidates = [
            taskNode?.updatedAt,
            taskNode?.completedAt,
            taskNode?.createdAt,
            action?.updatedAt,
            action?.createdAt,
          ].filter(Boolean);
          const latestDate =
            stampCandidates
              .map((value) => new Date(value))
              .filter((date) => !Number.isNaN(date.getTime()))
              .sort((a, b) => b - a)[0] || null;
          const dateKey = latestDate ? latestDate.toISOString().slice(0, 10) : "unknown";
          const title = cleanRailTaskTitle(
            taskNode?.task_title || taskNode?.title || action?.title || focusArea,
          );

          if (!byDate.has(dateKey)) byDate.set(dateKey, []);
          byDate.get(dateKey).push({
            id: `${actionId}:${taskId}`,
            actionId,
            title,
            outcomeLabel: isIgnoredTask(taskNode) ? "Skipped" : "Completed",
            focusArea,
            stamp: latestDate ? latestDate.getTime() : 0,
            row: {
              focusArea,
              actions: [action],
            },
            searchText: buildRailSearchText({
              focusArea,
              actions: [action],
              title,
              extra: [
                isProductRailAction(action) ? extractRailProductTitle(action) : "",
                isBlogRailAction(action) ? extractRailBlogTitle(action) : "",
                isIgnoredTask(taskNode) ? "skipped" : "completed",
              ],
            }),
          });
        });
      });
    });

    return Array.from(byDate.entries())
      .sort((a, b) => {
        if (a[0] === "unknown") return 1;
        if (b[0] === "unknown") return -1;
        return new Date(b[0]) - new Date(a[0]);
      })
      .map(([dateKey, tasks]) => ({
        dateLabel: formatLongDate(dateKey === "unknown" ? null : dateKey),
        tasks: tasks.sort((a, b) => b.stamp - a.stamp),
      }))
      .filter((group) => group.tasks.length > 0);
  }, [focusAreas]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openAssessment = (item) => {
    if (!hasCommerceConnection) {
      if (assessmentNavItem) {
        handleRightRailNav(assessmentNavItem);
      } else {
        navigate("/");
      }
      return;
    }
    if (!item?.id) {
      if (assessmentNavItem) handleRightRailNav(assessmentNavItem);
      return;
    }
    // "Daily Overview" opens the dedicated report instead of the generic assessment page
    if (item.id === "business") {
      openReport("dailyOverview");
      return;
    }
    navigate(`/assessment?summary=${encodeURIComponent(item.id)}`, {
      state: {
        assessmentHeaderSeed: buildAssessmentHeaderSeed(item),
      },
    });
  };

  const openStrategy = () => {
    if (strategyNavItem) handleRightRailNav(strategyNavItem);
  };

  const openReport = (key) => {
    if (!hasCommerceConnection) {
      navigate("/reports");
      return;
    }
    const traceId = buildReportOpenTraceId(key);
    const clickStartedAtMs = reportOpenNow();
    console.log("[report-open][frontend] click", { key, traceId, userId, shopId });

    // All reports navigate immediately to their open route — skeleton shows right away
    navigate(`/reports/open/${key}`, {
      state: {
        reportOpenTrace: {
          traceId,
          source: `${key}-route-click`,
          clickStartedAtMs,
        },
      },
    });
  };

  const openTaskModal = (task, mode) => {
    if (!task?.row) return;
    setSelectedTaskRow(task.row);
    setSelectedTaskActionId(task.actionId || null);
    setSelectedTaskModalTab(
      mode === "log"
        ? t("ZeqiKeyFocusArea.tabs.running")
        : task.isRunning
          ? t("ZeqiKeyFocusArea.tabs.running")
          : t("ZeqiKeyFocusArea.tabs.suggested"),
    );
  };

  return (
    <aside
      ref={ref}
      className={styles.rightRail}
      aria-label={t("AppShell.rightRailAria") || "Zeqi right rail"}
      data-tour-scroll-container="true"
    >
      <div className={styles.rightRailInsights}>
        <AppRightRailAssessment
          open={openSections.assessments}
          assessments={assessments}
          highlightedAssessmentIds={highlightedAssessmentIds}
          activeSummaryId={activeSummaryId}
          chatContextPickMode={chatContextPickMode}
          onToggle={() => toggleSection("assessments")}
          onOpenAssessment={openAssessment}
          styles={styles}
        />

        {/* <AppRightRailStrategy
          open={openSections.strategy}
          onToggle={() => toggleSection("strategy")}
          onOpenStrategy={openStrategy}
          styles={styles}
        /> */}

        <AppRightRailBrandKnowledge
          open={openSections.brand}
          chatContextPickMode={chatContextPickMode}
          onToggle={() => toggleSection("brand")}
          onNavigateTab={(tab) => navigate(`/brand?tab=${encodeURIComponent(tab)}`)}
          styles={styles}
        />

        <AppRightRailReportSection
          open={openSections.reports}
          reportCount={REPORTS.filter((report) => !report.comingSoon).length}
          reportsWithMeta={reportsWithMeta}
          highlightedReportKeys={highlightedReportKeys}
          openReportDropdown={openReportDropdown}
          chatContextPickMode={chatContextPickMode}
          onToggle={() => toggleSection("reports")}
          onToggleDropdown={setOpenReportDropdown}
          onOpenReport={openReport}
          styles={styles}
        />

        <AppRightRailTaskAgentSection
          taskAgentTab={taskAgentTab}
          activeTaskGroups={activeTaskGroups}
          taskLogGroups={taskLogGroups}
          onSetTaskAgentTab={setTaskAgentTab}
          onOpenTaskModal={openTaskModal}
          styles={styles}
        />
      </div>
      <SelectedActionModal
        isOpen={Boolean(selectedTaskRow)}
        row={selectedTaskRow}
        onClose={() => {
          setSelectedTaskRow(null);
          setSelectedTaskActionId(null);
          setSelectedTaskModalTab("");
        }}
        activeTab={selectedTaskModalTab}
        onUpdateRow={(updatedRow) => setSelectedTaskRow(updatedRow)}
        onActionCompleted={() => refetchActions?.()}
        expandActionId={selectedTaskActionId}
        onRunTask={(actionId) => setSelectedTaskActionId(actionId)}
        onRunAllStarted={() => {
          setTaskAgentTab("active");
          setSelectedTaskModalTab(t("ZeqiKeyFocusArea.tabs.running"));
        }}
      />
    </aside>
  );
});

export default AppRightRail;
